/**
 * Firebase client bootstrap.
 *
 * All configuration comes from Vite `import.meta.env.VITE_*` vars (see
 * `.env.example` and `vite-env.d.ts`). Exposes the singleton SDK handles the
 * rest of the app consumes: `app`, `auth`, `db`, `storage`, `functions`.
 *
 * - App Check (reCAPTCHA v3) is initialised only when a site key is provided.
 * - When `VITE_USE_EMULATORS === 'true'` every service is pointed at the local
 *   Firebase emulator suite so the whole app runs without cloud credentials.
 */
import { type FirebaseApp, initializeApp } from 'firebase/app'
import { type AppCheck, initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check'
import { type Auth, connectAuthEmulator, getAuth } from 'firebase/auth'
import { connectFirestoreEmulator, type Firestore, getFirestore } from 'firebase/firestore'
import { connectFunctionsEmulator, type Functions, getFunctions } from 'firebase/functions'
import { connectStorageEmulator, type FirebaseStorage, getStorage } from 'firebase/storage'

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
}

const FUNCTIONS_REGION = import.meta.env.VITE_FUNCTIONS_REGION ?? 'us-central1'
const USE_EMULATORS = import.meta.env.VITE_USE_EMULATORS === 'true'

// Falha rápido: sem estas vars o Firebase Auth fica inutilizável e o app quebra
// silenciosamente em runtime. Detectar no boot deixa a causa óbvia.
const REQUIRED_CONFIG: Array<[keyof typeof firebaseConfig, string]> = [
  ['apiKey', 'VITE_FIREBASE_API_KEY'],
  ['authDomain', 'VITE_FIREBASE_AUTH_DOMAIN'],
  ['projectId', 'VITE_FIREBASE_PROJECT_ID'],
  ['appId', 'VITE_FIREBASE_APP_ID'],
]
const missingConfig = REQUIRED_CONFIG.filter(([key]) => !firebaseConfig[key]).map(
  ([, envVar]) => envVar,
)
if (missingConfig.length > 0) {
  throw new Error(
    `Configuracao do Firebase ausente (${missingConfig.join(', ')}): build gerado sem apps/web/.env.local. Rode "pnpm --filter web build" com o .env.local presente antes de publicar.`,
  )
}

export const app: FirebaseApp = initializeApp(firebaseConfig)

/**
 * App Check — enabled only when a reCAPTCHA v3 site key is configured. Callables
 * enforce App Check on the backend, so leave the key unset for local emulator work
 * (where App Check enforcement is bypassed) and set it in production.
 */
export const appCheck: AppCheck | undefined = import.meta.env.VITE_FIREBASE_RECAPTCHA_SITE_KEY
  ? initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(import.meta.env.VITE_FIREBASE_RECAPTCHA_SITE_KEY),
      isTokenAutoRefreshEnabled: true,
    })
  : undefined

export const auth: Auth = getAuth(app)
export const db: Firestore = getFirestore(app)
export const storage: FirebaseStorage = getStorage(app)
export const functions: Functions = getFunctions(app, FUNCTIONS_REGION)

if (USE_EMULATORS) {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true })
  connectFirestoreEmulator(db, '127.0.0.1', 8080)
  connectStorageEmulator(storage, '127.0.0.1', 9199)
  connectFunctionsEmulator(functions, '127.0.0.1', 5001)
}
